import { Controller } from "@hotwired/stimulus";
import { sanitizeFilename } from "../utils/file_helpers";

export default class extends Controller {
  static targets = ["fileInput", "progress", "preview", "saveButton"];

  static values = {
    organizationId: String,
    winemakerId: String,
    vintageId: String,
    criticId: String,
    locationId: String,
    bulkImportId: String,
  };

  connect() {
    // Initialize save button as disabled
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.disabled = true;
      this.saveButtonTarget.classList.add("opacity-50", "cursor-not-allowed");
    }
  }

  triggerFileInput() {
    this.fileInputTarget.click();
  }

  async handleFileSelect(event) {
    const file = event.target.files[0];
    if (!file) return;

    // Show progress bar
    if (this.hasProgressTarget) {
      this.progressTarget.classList.remove("hidden");
      this.progressTarget.innerHTML = `
        <div class="mt-2">
          <div class="flex justify-between text-sm text-gray-600 mb-1">
            <span>Uploading</span>
            <span>0%</span>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div class="bg-blue-600 h-2 rounded-full transition-all duration-300" style="width: 0%"></div>
          </div>
        </div>
      `;
    }

    try {
      // 1. Get presigned POST data from Rails
      const presignResponse = await fetch("/presigned_urls", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            ?.content,
        },
        credentials: "same-origin",
      });

      if (!presignResponse.ok) {
        throw new Error(
          `Failed to get presigned URL: ${presignResponse.status}`,
        );
      }

      const presignData = await presignResponse.json();

      // Update progress to show upload starting
      if (this.hasProgressTarget) {
        this.progressTarget.innerHTML = `
          <div class="mt-2">
            <div class="flex justify-between text-sm text-gray-600 mb-1">
              <span>Uploading</span>
              <span>0%</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div class="bg-blue-600 h-2 rounded-full transition-all duration-300" style="width: 0%"></div>
            </div>
          </div>
        `;
      }

      // 2. Upload file to S3 with progress tracking
      // Sanitize filename to remove spaces and special characters for S3 compatibility
      const sanitizedFilename = sanitizeFilename(file.name);

      // Create a new File object with the sanitized name
      const sanitizedFile = new File([file], sanitizedFilename, {
        type: file.type,
      });

      const formData = new FormData();
      Object.entries(presignData.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append("file", sanitizedFile);

      // Create XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable && this.hasProgressTarget) {
          const percentComplete = Math.round(
            (event.loaded / event.total) * 100,
          );
          const progressBar = this.progressTarget.querySelector(".bg-blue-600");
          const percentText = this.progressTarget.querySelector(
            ".text-gray-600 span:last-child",
          );

          if (progressBar) progressBar.style.width = percentComplete + "%";
          if (percentText) percentText.textContent = percentComplete + "%";
        }
      });

      // Handle completion
      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          // 3. Set the S3 URL in the hidden field
          const s3Url = `${presignData.url}/${presignData.fields.key.replace(
            "${filename}",
            sanitizedFilename,
          )}`;
          this.element.dataset.attachmentUrl = s3Url;

          // Update progress to show completion
          if (this.hasProgressTarget) {
            this.progressTarget.innerHTML = `
              <div class="mt-2">
                <div class="flex justify-between text-sm text-green-600 mb-1">
                  <span>Upload Complete</span>
                  <span>100%</span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-2">
                  <div class="bg-green-600 h-2 rounded-full transition-all duration-300" style="width: 100%"></div>
                </div>
              </div>
            `;
          }

          // Show preview
          this.showPreview(file, s3Url);

          // Enable save button
          this.enableSaveButton();
        } else {
          throw new Error(`Upload failed: ${xhr.status}`);
        }
      });

      // Handle errors
      xhr.addEventListener("error", () => {
        throw new Error("Network error during upload");
      });

      // Start the upload
      xhr.open("POST", presignData.url);
      xhr.send(formData);
    } catch (error) {
      console.error("Upload error:", error);
      if (this.hasProgressTarget) {
        this.progressTarget.innerHTML = `
          <div class="mt-2">
            <div class="flex justify-between text-sm text-red-600 mb-1">
              <span>Upload failed: ${error.message}</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div class="bg-red-600 h-2 rounded-full transition-all duration-300" style="width: 100%"></div>
            </div>
          </div>
        `;
      }
      alert(`Upload failed: ${error.message}`);
    }
  }

  async saveAttachment() {
    const attachmentUrl = this.element.dataset.attachmentUrl;
    if (!attachmentUrl) {
      alert("Please upload a file first");
      return;
    }

    if (
      !this.organizationIdValue &&
      !this.winemakerIdValue &&
      !this.vintageIdValue &&
      !this.criticIdValue &&
      !this.locationIdValue &&
      !this.bulkImportIdValue
    ) {
      alert("No valid ID found for attachment upload");
      return;
    }

    // Disable save button during save operation
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.disabled = true;
      this.saveButtonTarget.textContent = "Saving...";
      this.saveButtonTarget.classList.add("opacity-50", "cursor-not-allowed");
    }

    try {
      const response = await fetch("/attachments", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            ?.content,
        },
        credentials: "same-origin",
        body: JSON.stringify({
          organization_id: this.organizationIdValue,
          winemaker_id: this.winemakerIdValue,
          vintage_id: this.vintageIdValue,
          critic_id: this.criticIdValue,
          location_id: this.locationIdValue,
          bulk_import_id: this.bulkImportIdValue,
          attachment_url: attachmentUrl,
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Show success message
        this.showSuccessMessage(result.message);

        // Clear the form
        this.clearForm();

        // Refresh the attachments list if it exists
        this.refreshAttachmentsList();
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error("Save error:", error);
      alert(`Failed to save attachment: ${error.message}`);
    } finally {
      // Re-enable save button
      if (this.hasSaveButtonTarget) {
        this.saveButtonTarget.disabled = false;
        this.saveButtonTarget.textContent = "Save Attachment";
        this.saveButtonTarget.classList.remove(
          "opacity-50",
          "cursor-not-allowed",
        );
      }
    }
  }

  showSuccessMessage(message) {
    // Create a temporary success message
    const successDiv = document.createElement("div");
    successDiv.className =
      "mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded";
    successDiv.textContent = message;

    // Insert after the form
    this.element.parentNode.insertBefore(successDiv, this.element.nextSibling);

    // Remove after 3 seconds
    setTimeout(() => {
      successDiv.remove();
    }, 3000);
  }

  clearForm() {
    // Clear file input
    if (this.hasFileInputTarget) {
      this.fileInputTarget.value = "";
    }

    // Clear attachment URL from data attribute
    delete this.element.dataset.attachmentUrl;

    // Hide progress and preview
    if (this.hasProgressTarget) {
      this.progressTarget.classList.add("hidden");
    }

    if (this.hasPreviewTarget) {
      this.previewTarget.classList.add("hidden");
    }

    // Disable save button
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.disabled = true;
      this.saveButtonTarget.classList.add("opacity-50", "cursor-not-allowed");
    }
  }

  refreshAttachmentsList() {
    // Find and refresh the attachments list if it exists
    const attachmentsList = document.querySelector(".file-boxes");
    if (attachmentsList) {
      // Simple page reload to show new attachment
      // The new attachments controller will handle all attachment management
      window.location.reload();
    }
  }

  showPreview(file, url) {
    if (!this.hasPreviewTarget) return;

    const preview = this.previewTarget;
    preview.classList.remove("hidden");

    if (file.type.startsWith("image/")) {
      // Show image preview
      preview.innerHTML = `
        <div class="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50 w-1/2">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-700">Preview</span>
            <button type="button" class="text-gray-400 hover:text-gray-600" onclick="this.closest('.mt-4').remove()">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <img src="${url}" alt="Preview" class="max-w-full h-auto max-h-48 rounded border">
          <div class="mt-2 text-xs text-gray-500">
            ${file.name} (${this.formatFileSize(file.size)})
          </div>
        </div>
      `;
    } else {
      // Show file info for non-images
      preview.innerHTML = `
        <div class="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50 w-1/2">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-700">File Ready</span>
            <button type="button" class="text-gray-400 hover:text-gray-600" onclick="this.closest('.mt-4').remove()">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <div class="flex items-center">
            <svg class="w-8 h-8 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            <div>
              <div class="text-sm font-medium text-gray-900">${file.name}</div>
              <div class="text-xs text-gray-500">${this.formatFileSize(
                file.size,
              )}</div>
            </div>
          </div>
        </div>
      `;
    }
  }

  enableSaveButton() {
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.disabled = false;
      this.saveButtonTarget.classList.remove(
        "opacity-50",
        "cursor-not-allowed",
      );
    }
  }

  formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }
}
